import React, { useState } from 'react';
import { CreditCard, Lock, Check, AlertCircle } from 'lucide-react';
import asaasService from '../../services/asaasService';
import { ResultadoPagamento } from '../../types/checkout';

interface CreditCardPaymentProps {
  cobranca: any;
  cliente: any;
  onSuccess: (resultado: ResultadoPagamento) => void;
  onError: (error: string) => void;
}

const CreditCardPayment: React.FC<CreditCardPaymentProps> = ({ 
  cobranca, 
  cliente,
  onSuccess, 
  onError 
}) => {
  const [formData, setFormData] = useState({
    cardNumber: '',
    cardName: '',
    expiryDate: '',
    cvv: '',
    installments: 1
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [asaasErrors, setAsaasErrors] = useState<string[]>([]);

  // Função para mapear erros do Asaas para mensagens amigáveis
  const mapearErroAsaas = (erro: any): string => {
    const codigo = erro.code;
    const descricao = erro.description;

    switch (codigo) {
      case 'invalid_creditCard':
        if (descricao.includes('CEP')) {
          return 'CEP inválido. Verifique o CEP informado.';
        }
        if (descricao.includes('email')) {
          return 'Email inválido. Verifique o email informado.';
        }
        if (descricao.includes('CPF')) {
          return 'CPF inválido. Verifique o CPF informado.';
        }
        if (descricao.includes('telefone') || descricao.includes('contato')) {
          return 'Telefone inválido. Verifique o telefone informado.';
        }
        return 'Dados do cartão inválidos. Verifique as informações.';
      
      case 'invalid_card_number':
        return 'Número do cartão inválido.';
      
      case 'invalid_expiry_date':
        return 'Data de validade inválida.';
      
      case 'invalid_cvv':
        return 'CVV inválido.';
      
      case 'insufficient_funds':
        return 'Saldo insuficiente no cartão.';
      
      case 'card_declined':
        return 'Cartão recusado. Entre em contato com seu banco.';
      
      default:
        return descricao || 'Erro no processamento do pagamento.';
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.cardNumber.replace(/\s/g, '')) {
      newErrors.cardNumber = 'Número do cartão é obrigatório';
    } else if (formData.cardNumber.replace(/\s/g, '').length < 13) {
      newErrors.cardNumber = 'Número do cartão inválido';
    }

    if (!formData.cardName.trim()) {
      newErrors.cardName = 'Nome no cartão é obrigatório';
    }

    if (!formData.expiryDate) {
      newErrors.expiryDate = 'Data de validade é obrigatória';
    } else if (!/^\d{2}\/\d{2}$/.test(formData.expiryDate)) {
      newErrors.expiryDate = 'Formato inválido (MM/AA)';
    }

    if (!formData.cvv) {
      newErrors.cvv = 'CVV é obrigatório';
    } else if (formData.cvv.length < 3) {
      newErrors.cvv = 'CVV inválido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const formatCardNumber = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    return numbers.replace(/(\d{4})(\d{4})(\d{4})(\d{4})/, '$1 $2 $3 $4');
  };

  const formatExpiryDate = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    return numbers.replace(/(\d{2})(\d{2})/, '$1/$2');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      // Processar pagamento imediatamente com os dados do cartão
      const dadosCartao = {
        holderName: formData.cardName,
        number: formData.cardNumber,
        expiryMonth: formData.expiryDate.split('/')[0],
        expiryYear: '20' + formData.expiryDate.split('/')[1],
        cvv: formData.cvv,
        email: cliente.email || '',
        cpfCnpj: cliente.cpfCnpj || '',
        postalCode: cliente.postalCode || '',
        addressNumber: cliente.addressNumber || '',
        phone: cliente.phone || ''
      };

      const resultado = await asaasService.processarPagamentoCartao(
        cobranca.customer,
        { 
          id: 'plano', 
          nome: 'Plano', 
          preco: cobranca.value,
          descricao: 'Plano de assinatura',
          funcionalidades: []
        },
        dadosCartao
      );
      
      onSuccess({
        success: true,
        paymentId: resultado.id,
        message: 'Pagamento com cartão processado com sucesso!'
      });
    } catch (error: any) {
      console.error('Erro ao processar pagamento:', error);
      
      // Limpar erros anteriores
      setAsaasErrors([]);
      
      // Verificar se é erro do Asaas com array de erros
      if (error.response?.data?.errors || error.errors) {
        const errosAsaas = error.response?.data?.errors || error.errors;
        const mensagensErro = errosAsaas.map((erro: any) => mapearErroAsaas(erro));
        setAsaasErrors(mensagensErro);
        onError(mensagensErro.join(' '));
      } else {
        // Erro genérico
        const mensagemErro = error instanceof Error ? error.message : 'Erro ao processar pagamento com cartão';
        setAsaasErrors([mensagemErro]);
        onError(mensagemErro);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto p-6">
      <div className="text-center mb-6">
        <div className="flex justify-center mb-4">
          <CreditCard className="w-12 h-12 text-blue-500" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">
          Pagamento com Cartão
        </h3>
        <p className="text-gray-600">
          Preencha os dados do seu cartão de crédito
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Número do cartão */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Número do Cartão *
          </label>
          <input
            type="text"
            value={formData.cardNumber}
            onChange={(e) => handleInputChange('cardNumber', formatCardNumber(e.target.value))}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.cardNumber ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="0000 0000 0000 0000"
            maxLength={19}
          />
          {errors.cardNumber && (
            <p className="text-red-500 text-sm mt-1">{errors.cardNumber}</p>
          )}
        </div>

        {/* Nome no cartão */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Nome no Cartão *
          </label>
          <input
            type="text"
            value={formData.cardName}
            onChange={(e) => handleInputChange('cardName', e.target.value.toUpperCase())}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.cardName ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="NOME COMO NO CARTÃO"
          />
          {errors.cardName && (
            <p className="text-red-500 text-sm mt-1">{errors.cardName}</p>
          )}
        </div>

        {/* Data de validade e CVV */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Validade *
            </label>
            <input
              type="text"
              value={formData.expiryDate}
              onChange={(e) => handleInputChange('expiryDate', formatExpiryDate(e.target.value))}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.expiryDate ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="MM/AA"
              maxLength={5}
            />
            {errors.expiryDate && (
              <p className="text-red-500 text-sm mt-1">{errors.expiryDate}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              CVV *
            </label>
            <input
              type="text"
              value={formData.cvv}
              onChange={(e) => handleInputChange('cvv', e.target.value.replace(/\D/g, ''))}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.cvv ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="123"
              maxLength={4}
            />
            {errors.cvv && (
              <p className="text-red-500 text-sm mt-1">{errors.cvv}</p>
            )}
          </div>
        </div>

        {/* Parcelamento */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Parcelamento
          </label>
          <select
            value={formData.installments}
            onChange={(e) => handleInputChange('installments', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(num => (
              <option key={num} value={num}>
                {num}x de R$ {(cobranca.value / num).toFixed(2)} sem juros
              </option>
            ))}
          </select>
        </div>

        {/* Erros do Asaas */}
        {asaasErrors.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 mr-2 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-medium text-red-800 mb-1">
                  Erro no processamento:
                </h4>
                <ul className="text-sm text-red-700 space-y-1">
                  {asaasErrors.map((erro, index) => (
                    <li key={index}>• {erro}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Botão de pagamento */}
        <button
          type="submit"
          disabled={loading}
          className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Processando...
            </>
          ) : (
            <>
              <Lock className="w-4 h-4 mr-2" />
              Pagar R$ {cobranca.value.toFixed(2)}
            </>
          )}
        </button>
      </form>

      {/* Informações de segurança */}
      <div className="mt-6 bg-gray-50 rounded-lg p-4">
        <div className="flex items-center mb-2">
          <Lock className="w-4 h-4 text-green-500 mr-2" />
          <span className="text-sm font-medium text-gray-900">Pagamento Seguro</span>
        </div>
        <div className="text-xs text-gray-600 space-y-1">
          <p>• Dados criptografados e protegidos</p>
          <p>• Processamento via Asaas</p>
          <p>• Certificado PCI-DSS</p>
        </div>
      </div>

      {/* Bandeiras aceitas */}
      <div className="mt-4 text-center">
        <p className="text-xs text-gray-500 mb-2">Bandeiras aceitas:</p>
        <div className="flex justify-center space-x-2">
          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">VISA</span>
          <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">MASTERCARD</span>
          <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">ELO</span>
        </div>
      </div>
    </div>
  );
};

export default CreditCardPayment;
